import { logger } from '../utils/ProfessionalLogger';
/**
 * ErrorHandler.ts;
 * Gestionnaire d'erreurs centralisé et robuste;
 */
interface ErrorContext {
  component: string;
  action: string;
  timestamp: number;
  userAgent: string;
  url: string;
  stack?: string;
}
interface ErrorReport {
  id: string;
  message: string;
  context: ErrorContext;
  severity: 'low' | 'medium' | 'high' | 'critical';
  resolved: boolean;
  occurrences: number;
}
export class ErrorHandler {
  private static instance: ErrorHandler;
  private errors: ErrorReport[] = [];
  private maxErrors = 100;
  private isReportingEnabled = true;
  private constructor() {
    this.setupGlobalErrorHandlers();
  }
  /**
   * Obtient l'instance singleton;
   */
  public static getInstance(): ErrorHandler {
    if (!ErrorHandler.instance) {
      ErrorHandler.instance = new ErrorHandler();
    }
    return ErrorHandler.instance;
  }
  /**
   * Configure les gestionnaires d'erreurs globaux;
   */
  private setupGlobalErrorHandlers(): void {
    // Erreurs JavaScript non capturées;
    window.addEventListener('error', event => {
      this.handleError(event.error, {
        component: 'Global',
        action: 'Uncaught Error',
        timestamp: Date.now(),
        userAgent: navigator.userAgent,
        url: window.location.href,
        stack: event.error?.stack,
      });
    });
    // Promesses rejetées non capturées;
    window.addEventListener('unhandledrejection', event => {
      this.handleError(event.reason, {
        component: 'Global',
        action: 'Unhandled Promise Rejection',
        timestamp: Date.now(),
        userAgent: navigator.userAgent,
        url: window.location.href,
      });
    });
    // Erreurs de ressources (images, scripts, etc.)
    window.addEventListener(
      'error',
      event => {
        if (event.target !== window) {
          this.handleError(
            new Error(
              `Resource loading failed: ${(event.target as { src?: string; href?: string }).src || (event.target as { src?: string; href?: string }).href}`
            ),
            {
              component: 'Resource',
              action: 'Load Failed',
              timestamp: Date.now(),
              userAgent: navigator.userAgent,
              url: window.location.href,
            }
          );
        }
      },
      true
    );
  }
  /**
   * Gère une erreur;
   */
  public handleError(error: Error | unknown, context: Partial<ErrorContext> = {}): void {
    const errorReport: ErrorReport = {
      id: this.generateErrorId(),
      message: this.extractErrorMessage(error),
      context: {
        component: context.component || 'Unknown',
        action: context.action || 'Unknown',
        timestamp: context.timestamp || Date.now(),
        userAgent: context.userAgent || navigator.userAgent,
        url: context.url || window.location.href,
        // CORRIGÉ: Vérifier le type de error avant d'accéder à stack
        stack:
          context.stack ||
          (error && typeof error === 'object' && 'stack' in error
            ? (error as { stack?: string }).stack
            : undefined),
      },
      severity: this.determineSeverity(error, context),
      resolved: false,
      occurrences: 1,
    };
    // Vérifier si cette erreur existe déjà
    const existingError = this.findSimilarError(errorReport);
    if (existingError) {
      existingError.occurrences++;
      existingError.context.timestamp = Date.now();
    } else {
      this.errors.push(errorReport);
      // Limiter le nombre d'erreurs stockées;
      if (this.errors.length > this.maxErrors) {
        this.errors = this.errors.slice(-this.maxErrors);
      }
    }
    // Logger l'erreur;
    this.logError(errorReport);
    // Reporter l'erreur si nécessaire;
    if (this.isReportingEnabled && errorReport.severity === 'critical') {
      this.reportError(errorReport);
    }
  }
  /**
   * Gère une erreur avec un message personnalisé
   */
  public handleCustomError(
    message: string,
    context: Partial<ErrorContext> = {},
    _severity: ErrorReport['severity'] = 'medium'
  ): void {
    const error = new Error(message);
    this.handleError(error, context);
  }
  /**
   * Gère une erreur de composant;
   */
  public handleComponentError(component: string, action: string, error: Error | unknown): void {
    this.handleError(error, {
      component,
      action,
    });
  }
  /**
   * Gère une erreur d'API;
   */
  public handleAPIError(endpoint: string, method: string, error: Error | unknown): void {
    this.handleError(error, {
      component: 'API',
      action: `${method} ${endpoint}`,
    });
  }
  /**
   * Gère une erreur de rendu;
   */
  public handleRenderError(component: string, error: Error | unknown): void {
    this.handleError(error, {
      component,
      action: 'Render',
    });
  }
  /**
   * Obtient toutes les erreurs;
   */
  public getErrors(): ErrorReport[] {
    return [...this.errors];
  }
  /**
   * Obtient les erreurs par sévérité
   */
  public getErrorsBySeverity(severity: ErrorReport['severity']): ErrorReport[] {
    return this.errors.filter(error => error.severity === severity);
  }
  /**
   * Obtient les erreurs par composant;
   */
  public getErrorsByComponent(component: string): ErrorReport[] {
    return this.errors.filter(error => error.context.component === component);
  }
  /**
   * Marque une erreur comme résolue;
   */
  public resolveError(errorId: string): void {
    const error = this.errors.find(e => e.id === errorId);
    if (error) {
      error.resolved = true;
    }
  }
  /**
   * Supprime une erreur;
   */
  public removeError(errorId: string): void {
    this.errors = this.errors.filter(e => e.id !== errorId);
  }
  /**
   * Vide toutes les erreurs;
   */
  public clearErrors(): void {
    this.errors = [];
  }
  /**
   * Active/désactive le reporting d'erreurs;
   */
  public setReportingEnabled(enabled: boolean): void {
    this.isReportingEnabled = enabled;
  }
  /**
   * Obtient les statistiques d'erreurs;
   */
  public getErrorStats(): {
    total: number;
    bySeverity: Record<string, number>;
    byComponent: Record<string, number>;
    unresolved: number;
  } {
    const bySeverity = this.errors.reduce(
      (acc, error) => {
        acc[error.severity] = (acc[error.severity] || 0) + 1;
        return acc;
      },
      {} as Record<string, number>
    );
    const byComponent = this.errors.reduce(
      (acc, error) => {
        acc[error.context.component] = (acc[error.context.component] || 0) + 1;
        return acc;
      },
      {} as Record<string, number>
    );
    return {
      total: this.errors.length,
      bySeverity,
      byComponent,
      unresolved: this.errors.filter(e => !e.resolved).length,
    };
  }
  /**
   * Exporte les erreurs en JSON;
   */
  public exportErrors(): string {
    return JSON.stringify(
      {
        errors: this.errors,
        stats: this.getErrorStats(),
        timestamp: Date.now(),
      },
      null,
      2
    );
  }
  /**
   * Extrait le message d'erreur;
   */
  private extractErrorMessage(error: Error | unknown): string {
    if (error instanceof Error) {
      return error.message;
    }
    if (typeof error === 'string') {
      return error;
    }
    // CORRIGÉ: Vérifier le type de error avant d'accéder à message
    if (error && typeof error === 'object' && 'message' in error) {
      return (error as { message: string }).message;
    }
    return 'Unknown error';
  }
  /**
   * Détermine la sévérité d'une erreur;
   */
  private determineSeverity(
    error: unknown,
    context: Partial<ErrorContext>
  ): ErrorReport['severity'] {
    // Erreurs critiques;
    if (error instanceof TypeError && error.message.includes('Cannot read property')) {
      return 'critical';
    }
    if (error instanceof ReferenceError) {
      return 'critical';
    }
    if (context.component === 'Canvas' || context.component === 'Konva') {
      return 'high';
    }
    if (context.component === 'API') {
      return 'medium';
    }
    return 'low';
  }
  /**
   * Trouve une erreur similaire;
   */
  private findSimilarError(newError: ErrorReport): ErrorReport | undefined {
    return this.errors.find(
      existing =>
        existing.message === newError.message &&
        existing.context.component === newError.context.component &&
        existing.context.action === newError.context.action
    );
  }
  /**
   * Génère un ID unique pour l'erreur;
   */
  private generateErrorId(): string {
    return `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
  /**
   * Log l'erreur dans la console;
   */
  private logError(errorReport: ErrorReport): void {
    const logLevel = this.getLogLevel(errorReport.severity);
    const message = `🚨 [${errorReport.severity.toUpperCase()}] ${errorReport.context.component}: ${errorReport.message}`;
    switch (logLevel) {
      case 'error':
        logger.error(message, errorReport);
        break;
      case 'warn':
        logger.warn(message, errorReport);
        break;
      case 'info':
        logger.info(message, errorReport);
        break;
      default:
        logger.info(message, errorReport);
    }
  }
  /**
   * Détermine le niveau de log;
   */
  private getLogLevel(severity: ErrorReport['severity']): 'error' | 'warn' | 'info' | 'log' {
    switch (severity) {
      case 'critical':
      case 'high':
        return 'error';
      case 'medium':
        return 'warn';
      case 'low':
        return 'info';
      default:
        return 'log';
    }
  }
  /**
   * Reporte une erreur critique;
   */
  private reportError(errorReport: ErrorReport): void {
    // Ici, vous pourriez envoyer l'erreur à un service de monitoring;
    // comme Sentry, LogRocket, ou un service personnalisé
    logger.error('📊 Error reported:', errorReport);
  }
}
// Instance globale du gestionnaire d'erreurs;
export const errorHandler = ErrorHandler.getInstance();
